
import React from 'react';
import { motion } from 'framer-motion';
import { Activity, MessageSquare, Stethoscope, BarChart3 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const WhatWeDoSection = () => {
  const isMobile = useIsMobile();
  
  const steps = [
    {
      icon: <Activity className="h-10 w-10 text-morgenBlue" />,
      title: "Gather Signals",
      description: "Gather comprehensive behavior and phenotype data to build a complete profile."
    },
    {
      icon: <MessageSquare className="h-10 w-10 text-morgenBlue" />,
      title: "Deliver Nudges",
      description: "Deliver timely daily and weekly nudges tailored to individual needs."
    },
    {
      icon: <Stethoscope className="h-10 w-10 text-morgenViolet" />,
      title: "Route to Care",
      description: "Drive labs, CPT 99487, PCP follow-ups and other billable activities."
    },
    {
      icon: <BarChart3 className="h-10 w-10 text-morgenViolet" />,
      title: "Demonstrate ROI",
      description: "Track risk scores and demonstrate meaningful PMPM cost reduction."
    }
  ];

  return (
    <section id="what-we-do" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">What Morgen Does</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Morgen bridges consumer engagement and clinical outcomes — creating ROI across 
            member experience, Stars, and risk adjustment.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 relative">
          {/* Horizontal connector line - only visible on desktop */}
          {!isMobile && (
            <div className="absolute hidden lg:block top-1/3 left-0 right-0 h-0.5 bg-gradient-to-r from-morgenBlue via-morgenViolet to-morgenViolet/70 z-0"></div>
          )}
          
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow relative z-10"
            >
              <div className="relative z-10">
                <div className="bg-gray-50 rounded-xl p-3 inline-flex mb-4">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
              <div className="absolute right-0 bottom-0 w-24 h-24 bg-gradient-to-tl from-gray-50 to-white opacity-50 rounded-tl-full"></div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="p-6 bg-gradient-to-r from-blue-50/50 to-purple-50/50 rounded-xl inline-block border border-gray-100">
            <p className="text-xl font-medium text-gray-800">
              "Proactive, personalized care saves lives — and money."
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
